import { defineStore } from "pinia";
import {
    fetchClubs,
    fetchClubById,
    fetchClubMessages as apiFetchClubMessages,
    joinClub as apiJoinClub,
    leaveClub as apiLeaveClub,
    muteClub as apiMuteClub,
    unmuteClub as apiUnmuteClub,
    type ApiClubListItem,
    type ApiClubDetail,
    type ClubMessage,
    type ApiClubMessage,
} from "@/api/clubs";
import { fileUrl } from "@/utils/fileUrl";

// Сообщения клуба не сохраняем в localStorage (приватные данные)
const MAX_MESSAGES_PER_CLUB = 150;
const CLUBS_TTL = 60_000;
const DETAIL_TTL = 30_000;

function mapApiMessage(m: ApiClubMessage): ClubMessage {
    return {
        id: m.id,
        club_id: m.club_id,
        created_at: m.created_at,
        sender_id: m.sender?.id ?? "",
        sender_name: m.sender?.name,
        sender_avatar: fileUrl(m.sender?.avatar_id ?? undefined),
        message: m.message,
        type: m.type,
        is_read: m.is_read,
        file_url: m.file_url ? fileUrl(m.file_url) : undefined,
        file_extension: m.file_extension ?? undefined,
        is_image: m.is_image,
        is_video: m.is_video,
        is_document: m.is_document,
        reactions: m.reactions ?? [],
        my_reaction: m.my_reaction ?? undefined,
        reply_to: m.reply_to ?? null,
        reply_to_message_id: m.reply_to_message_id ?? m.reply_to?.id ?? null,
        is_deleted: !!m.is_deleted,
        deleted_at: m.deleted_at ?? null,
        is_edited: !!m.is_edited,
        edited_at: m.edited_at ?? null,
    };
}

export const useClubsStore = defineStore("clubs", {
    state: () => ({
        clubs: [] as ApiClubListItem[],
        lastClubsFetch: 0,

        details: {} as Record<number, ApiClubDetail>,
        detailFetchedAt: {} as Record<number, number>,

        messages: {} as Record<number, ClubMessage[]>,
        nextCursors: {} as Record<number, number | null>,
        hasMore: {} as Record<number, boolean>,

        loading: false,
        loadingDetail: false,
        loadingMessages: {} as Record<number, boolean>,
    }),

    getters: {
        hasUnread: (state) => state.clubs.some((c) => (c as any).unread_count > 0),

        clubById: (state) => (id: number): ApiClubListItem | undefined =>
            state.clubs.find((c) => c.id === id),

        detailById: (state) => (id: number): ApiClubDetail | null =>
            state.details[id] ?? null,

        messagesByClub: (state) => (clubId: number): ClubMessage[] =>
            state.messages[clubId] ?? [],

        isMember: (state) => (clubId: number): boolean =>
            state.details[clubId]?.is_member ?? false,

        isMuted: (state) => (clubId: number): boolean =>
            state.details[clubId]?.is_muted ?? false,
    },

    actions: {

        async fetchClubs(force = false) {
            const now = Date.now();
            if (!force && this.clubs.length > 0 && now - this.lastClubsFetch < CLUBS_TTL) {
                return this.clubs;
            }
            this.loading = this.clubs.length === 0;
            try {
                this.clubs = await fetchClubs();
                this.lastClubsFetch = now;
                return this.clubs;
            } catch (e) {
                console.error("[ClubsStore] fetchClubs error:", e);
                throw e;
            } finally {
                this.loading = false;
            }
        },

        async fetchClubDetail(clubId: number, force = false) {
            const now = Date.now();
            const cached = this.details[clubId];
            const fetchedAt = this.detailFetchedAt[clubId] ?? 0;
            if (!force && cached && now - fetchedAt < DETAIL_TTL) {
                return cached;
            }
            this.loadingDetail = true;
            try {
                const detail = await fetchClubById(clubId);
                this.details[clubId] = detail;
                this.detailFetchedAt[clubId] = now;
                return detail;
            } catch (e) {
                console.error("[ClubsStore] fetchClubDetail error:", e);
                throw e;
            } finally {
                this.loadingDetail = false;
            }
        },

        async fetchMessages(clubId: number) {
            this.loadingMessages[clubId] = true;
            try {
                const res = await apiFetchClubMessages(clubId, { limit: 30 });
                this.messages[clubId] = res.messages.map(mapApiMessage);
                this.nextCursors[clubId] = res.next_cursor;
                this.hasMore[clubId] = res.next_cursor !== null;
                return this.messages[clubId];
            } catch (e) {
                console.error("[ClubsStore] fetchMessages error:", e);
                throw e;
            } finally {
                this.loadingMessages[clubId] = false;
            }
        },

        async fetchMoreMessages(clubId: number) {
            const cursor = this.nextCursors[clubId];
            if (!cursor || !this.hasMore[clubId] || this.loadingMessages[clubId]) return;

            this.loadingMessages[clubId] = true;
            try {
                const res = await apiFetchClubMessages(clubId, { cursor, limit: 30 });
                const older = res.messages.map(mapApiMessage);
                const current = this.messages[clubId] ?? [];
                const combined = [...older, ...current];
                this.messages[clubId] = combined.slice(-MAX_MESSAGES_PER_CLUB);
                this.nextCursors[clubId] = res.next_cursor;
                this.hasMore[clubId] = res.next_cursor !== null;
                return older;
            } catch (e) {
                console.error("[ClubsStore] fetchMoreMessages error:", e);
                throw e;
            } finally {
                this.loadingMessages[clubId] = false;
            }
        },

        addMessage(clubId: number, msg: ClubMessage) {
            if (!this.messages[clubId]) this.messages[clubId] = [];

            const id = msg.id;
            console.log("[ClubsStore] addMessage clubId:", clubId, "| msg.id:", id, "| sender:", msg.sender_name, "| text:", msg.message?.slice(0, 30));

            if (id && typeof id === 'number' && id < 1_000_000_000_000) {
                const existIdx = this.messages[clubId].findIndex(m => m.id === id);
                if (existIdx !== -1) {
                    console.log("[ClubsStore] DUPLICATE — merging at index:", existIdx);
                    this.messages[clubId][existIdx] = { ...this.messages[clubId][existIdx], ...msg };
                    return;
                }
            }

            this.messages[clubId].push(msg);
            console.log("[ClubsStore] message pushed, total:", this.messages[clubId].length);
            if (this.messages[clubId].length > MAX_MESSAGES_PER_CLUB) {
                this.messages[clubId] = this.messages[clubId].slice(-MAX_MESSAGES_PER_CLUB);
            }
        },

        updateReaction(clubId: number, msgId: number, counts: ClubMessage["reactions"], myReaction?: string) {
            const msgs = this.messages[clubId];
            if (!msgs) return;
            const msg = msgs.find((m) => m.id === msgId);
            if (!msg) return;
            msg.reactions = counts ?? [];
            msg.my_reaction = myReaction;
        },

        updateMessage(clubId: number, messageId: number, patch: Partial<ClubMessage>) {
            const msgs = this.messages[clubId];
            if (!msgs) return;
            const idx = msgs.findIndex(m => m.id === messageId);
            if (idx === -1) return;
            this.messages[clubId][idx] = { ...this.messages[clubId][idx], ...patch };
        },

        removeMessage(clubId: number, messageId: number) {
            const msgs = this.messages[clubId];
            if (!msgs) return;
            this.messages[clubId] = msgs.filter(m => m.id !== messageId);
        },

        setUnreadCount(clubId: number, count: number) {
            const club = this.clubs.find(c => c.id === clubId);
            if (club) club.unread_count = count;
        },

        async joinClub(clubId: number) {
            await apiJoinClub(clubId);
            if (this.details[clubId]) this.details[clubId].is_member = true;
            const listItem = this.clubs.find((c) => c.id === clubId);
            if (listItem) listItem.is_member = true;
        },

        async leaveClub(clubId: number) {
            await apiLeaveClub(clubId);
            if (this.details[clubId]) this.details[clubId].is_member = false;
            const listItem = this.clubs.find((c) => c.id === clubId);
            if (listItem) listItem.is_member = false;
        },


        async muteClub(clubId: number) {
            await apiMuteClub(clubId);
            if (this.details[clubId]) this.details[clubId].is_muted = true;
            const listItem = this.clubs.find((c) => c.id === clubId);
            if (listItem) listItem.is_muted = true;
        },

        async unmuteClub(clubId: number) {
            await apiUnmuteClub(clubId);
            if (this.details[clubId]) this.details[clubId].is_muted = false;
            const listItem = this.clubs.find((c) => c.id === clubId);
            if (listItem) listItem.is_muted = false;
        },


        clearMessages(clubId: number) {
            delete this.messages[clubId];
            delete this.nextCursors[clubId];
            delete this.hasMore[clubId];
        },

        resetStore() {
            this.$reset();
        },
    },

    persist: {
        key: "clubs-store",
        storage: sessionStorage,
        pick: ["clubs", "lastClubsFetch", "details", "detailFetchedAt"],
    },
});
